import { useDispatch, useSelector } from 'react-redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { NotificationDialog } from '../../../CommonFunctions/DialogComponents';
import {
  alpha,
  ListItem,
  styled,
  Switch,
  Box,
  TextField,
  FormControlLabel,
  FormGroup,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  addResultForIfElse,
  deleteResult,
} from '../../../../redux/Calculations/CalculationsActions';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { fontSize, palette } from '../../../..';
import {
  handleKeyDown,
  handleDecimalInput,
  checkIfMissingColumn,
} from '../../../CommonFunctions/CommonFunctions';
import { RootState } from '../../../../redux';
import { WarningAmber } from '@mui/icons-material';
interface IResultCard {
  sourceId: string;
  propKey: string;
  sourceType: string;
  disabled?: boolean;
  isFlowUsedInOtherFlowsAsSource: boolean;
}

const TillDateSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    minWidth: 20, // Ensures the thumb remains circular
    minHeight: 20, // Ensures the thumb remains circular
    borderRadius: '50%',
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#2bb9bb',
    '&:hover': {
      backgroundColor: alpha('#2bb9bb', theme.palette.action.hoverOpacity),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#2',
  },
}));

const ResultCard = ({
  sourceId,
  sourceType,
  propKey,
  disabled,
  isFlowUsedInOtherFlowsAsSource,
}: IResultCard) => {
  const [name, setName] = useState<string>(sourceId);
  const [alert, setAlert] = useState<any>(null);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { chartProperties, tabTileProps, currentCalculationSession } = useSelector(
    (state: RootState) => {
      return {
        tabTileProps: state.tabTileProps,
        chartProperties: state.chartProperties,
        currentCalculationSession:
          state.calculations.properties[propKey]?.currentCalculationSession,
      };
    },
  );

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  const activeCondition = currentCalculationSession?.activeCondition;
  const activeFlow = currentCalculationSession?.activeFlow;

  const getName = (): string | null => {
    if (!currentCalculationSession || !currentCalculationSession.calculationInfo) {
      return null;
    }

    if (sourceType === 'field' || sourceType === 'flow') {
      if (sourceType === 'flow') {
        return currentCalculationSession.flowDisplayNames[sourceId];
      } else {
        return currentCalculationSession.calculationInfo.fields[sourceId].displayName;
      }
    }

    return sourceId;
  };

  const TrueFalse = () => {
    return (
      <FormGroup
        sx={{
          marginLeft: '6px',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          sx={{
            justifyContent: 'flex-start',
            gap: '0.5rem',
          }}
        >
          <Typography>False</Typography>
          <FormControlLabel
            value="end"
            control={
              <TillDateSwitch
                checked={sourceId.toLowerCase() === 'true'}
                onChange={(e) => {
                  if (disabled) return;
                  dispatch(
                    addResultForIfElse(
                      propKey,
                      activeFlow!,
                      activeCondition!,
                      e.target.checked ? 'true' : 'false',
                      sourceType,
                    ),
                  );
                }}
              />
            }
            label={<Typography>True</Typography>}
            labelPlacement="end"
          />
        </Box>
      </FormGroup>
    );
  };

  const CancelIcon = () => {
    return isFlowUsedInOtherFlowsAsSource ? (
      <Tooltip
        title="This calculation is being used in another flow. Please remove the dependency before deleting."
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '12px',
            },
          },
        }}
      >
        <LockOutlinedIcon
          style={{
            color: '#2BB9BB',
          }}
        />
      </Tooltip>
    ) : (
      <CloseRoundedIcon
        onClick={() => {
          if (disabled) return;
          if (!currentCalculationSession?.calculationInfo) return;

          const flows = Object.keys(currentCalculationSession.calculationInfo.flows);

          let shouldReturn = false;

          outerLoop: for (const flow of flows) {
            for (const condition of currentCalculationSession.calculationInfo.flows[flow]) {
              if (condition.source.includes(activeFlow)) {
                setAlert({
                  severity: 'warning',
                  message: 'Cannot delete the result as this flow is used in another flow',
                });
                shouldReturn = true;
                break outerLoop; // This breaks out of both loops
              }
            }
          }

          if (!shouldReturn) {
            dispatch(deleteResult(propKey, activeFlow!, activeCondition!));
          }
        }}
        sx={{
          fontSize: fontSize.medium,
          cursor: 'pointer',
          color: palette.primary.contrastText,
          '&:hover': {
            color: 'red',
            transform: 'scale(1.1)',
          },
        }}
      />
    );
  };

  useEffect(() => {
    if (sourceType !== 'field'||!currentCalculationSession) return;
    (async () => {
      const res = await checkIfMissingColumn(
        currentCalculationSession.calculationInfo.fields[sourceId],
        selectedDs,
        tabTileProps.tablesForSelectedDataSets[selectedDs.id],
      );
      setIsShowWarning(res.isMissing);
    })();
  }, [sourceId]);

  if (!currentCalculationSession) return null;

  return (
    <ListItem
      sx={{
        padding: '0px',
      }}
    >
      {/* <CancelIcon/> */}
      {/* <button
        type="button"
        className="buttonCommon columnClose"
        onClick={() => {
          deleteResult(propKey, activeFlow!, activeCondition!);
        }}
        title="Remove field"
      > */}
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      {/* </button> */}

      {sourceType === 'field' || sourceType === 'flow' ? (
        <div
          className="axisFilterField"
          style={{
            border: isShowWarning ? '1px solid #850000ff' : '1px solid grey',
            borderRadius: '5px',
            paddingLeft: '0.3rem',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '1.5rem',
            width: '100%',
            gap: '0.2rem',
            boxSizing: 'border-box',
          }}
        >
          <CancelIcon />
          <span
            style={{
              lineHeight: '15px',
              fontSize: fontSize.medium,
              color: palette.primary.contrastText,
            }}
          >
            {getName()}
          </span>
        </div>
      ) : sourceType === 'date' ? (
        <Box
          className="axisFilterField"
          sx={{
            border: '1px solid grey',
            borderRadius: '5px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '1.5rem',
            width: '100%',
            gap: '0.5rem',
            paddingInline: '0.2rem',
            paddingLeft: '0.3rem',
            '&.axisFilterField': {
              width: 'inherit',
            },
          }}
        >
          <CancelIcon />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              enableAccessibleFieldDOMStructure={false}
              format="yyyy-MM-dd"
              value={parseISO(sourceId || new Date().toISOString().split('T')[0])}
              onChange={(newValue) => {
                console.log('newValue', newValue);
                console.log('type  ', typeof newValue);
                if (disabled) return;
                if (!newValue) return;
                console.log('will update redux');
                dispatch(
                  addResultForIfElse(
                    propKey,
                    activeFlow!,
                    activeCondition!,
                    newValue?.toISOString().split('T')[0],
                    sourceType,
                  ),
                );
              }}
              sx={{ outline: 'none', border: 'none' }}
              slots={{
                textField: (params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        ...params.InputProps?.style,
                        height: '1rem', // Set a fixed height to match ListItem
                        fontSize: '0.8rem', // Adjust font size if needed
                        outline: 'none',
                        border: 'none',
                      },
                    }}
                    className="customDatePickerHeight"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Remove border from the notched outline
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Ensure no border on hover
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none', // Ensure no border when focused
                      },
                      marginLeft: '0rem',
                    }}
                  />
                ),
              }}
            />
          </LocalizationProvider>
        </Box>
      ) : sourceType === 'boolean' ? (
        <Box
          className="axisFilterField"
          sx={{
            border: '1px solid grey',
            borderRadius: '5px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '1.5rem',
            width: '100%',
            gap: '0.5rem',
            paddingInline: '0.2rem',
            '&.axisFilterField': {
              width: 'inherit',
            },
          }}
        >
          <CancelIcon />
          <TrueFalse />
        </Box>
      ) : (
        <TextField
          // autoFocus
          className="editTabSelected editTabSelected_p-0 editTabSelected_m-0"
          value={name}
          type={
            sourceType === 'integer' ? 'number' : sourceType === 'decimal' ? 'text' : sourceType
          }
          inputProps={{
            'aria-label':
              sourceType === 'integer'
                ? 'Enter a Number'
                : sourceType === 'decimal'
                  ? 'Enter a decimal number'
                  : `Enter ${sourceType}`,
          }}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CancelIcon />
              </InputAdornment>
            ),
            style: {
              height: '1.5rem',
              lineHeight: '1rem',
              width: '100%',
              border: 'none',
              outline: 'none',
              color: 'rgb(128, 128, 128)',
              paddingLeft: '4px',
            },
          }}
          onBlur={(e) => {
            if (disabled) return;
            dispatch(addResultForIfElse(propKey, activeFlow!, activeCondition!, name, sourceType));
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-input': {
              padding: '16.5px 14px 16.5px 4px', // Consistent padding for all browsers
            },
            // Firefox-specific styling
            '@supports (-moz-appearance: none)': {
              '& .MuiOutlinedInput-input': {
                padding: '16.5px 2px 16.5px 4px',
              },
            },
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (disabled) return;
            if (sourceType === 'integer') {
              handleKeyDown(event);
            }
            if (event.key === 'Enter') {
              dispatch(
                addResultForIfElse(propKey, activeFlow!, activeCondition!, name, sourceType),
              );
            }
          }}
          onChange={(e) => {
            if (disabled) return;
            let value = e.target.value;
            if (sourceType === 'decimal') {
              let isDecimalInput = handleDecimalInput(value);
              isDecimalInput && setName(value);
            } else {
              setName(value);
            }
          }}
        />
      )}
    </ListItem>
  );
};

export default ResultCard;
